import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, MessageCircle, MapPin, Phone } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/lib/site";

const NAV_ROUTES = [
    { to: "/ueber-mich", key: "about" },
    { to: "/methodik", key: "methods" },
    { to: "/dorn-breuss", key: "therapy" },
    { to: "/programme", key: "programs" },
    { to: "/kennenlerngespraech", key: "booking" },
    { to: "/analyse", key: "analysis" },
    { to: "/journal", key: "blog" },
];

const Footer = () => {
    const { t } = useLanguage();
    const year = new Date().getFullYear();

    return (
        <footer
            data-testid="site-footer"
            className="bg-ella-onyx text-ella-cream/80 pt-20 pb-10 relative overflow-hidden"
        >
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
                <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-ella-gold blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-ella-gold blur-3xl" />
            </div>
            <div className="ella-container relative">
                {/* CTA line */}
                <div className="pb-14 mb-14 border-b border-white/10 text-center">
                    <p className="font-display italic text-3xl md:text-4xl text-ella-champagne max-w-3xl mx-auto">
                        “{t.footer.quote}”
                    </p>
                    <p className="mt-6 text-sm text-ella-cream/60 max-w-xl mx-auto">
                        {t.footer.cta_line}
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-white/10">
                    <div className="lg:col-span-5">
                        <div data-testid="footer-brand">
                            <span className="block w-10 h-px bg-ella-gold/60 mb-5" aria-hidden="true" />
                            <p className="font-display text-4xl tracking-[0.28em] leading-none">ELLA</p>
                            <p className="text-[11px] uppercase tracking-[0.34em] text-ella-gold mt-3">
                                Online Coaching
                            </p>
                        </div>
                        <p className="mt-7 font-display text-lg text-ella-champagne italic tracking-wide">
                            {t.footer.tagline}
                        </p>
                        <p className="mt-6 text-sm leading-relaxed text-ella-cream/60 max-w-sm">
                            Premium Transformation Coaching aus der Schweiz — für Frauen, Männer und Familien, die bereit sind, ihr Leben neu zu gestalten.
                        </p>
                    </div>

                    <div className="lg:col-span-2">
                        <h4 className="ella-overline-light">{t.footer.navigation}</h4>
                        <ul className="mt-5 space-y-3 text-sm">
                            {NAV_ROUTES.map((r) => (
                                <li key={r.to}>
                                    <Link
                                        to={r.to}
                                        data-testid={`footer-route-${r.key}`}
                                        className="hover:text-ella-gold transition-colors"
                                    >
                                        {t.nav[r.key]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-2">
                        <h4 className="ella-overline-light">{t.footer.legal}</h4>
                        <ul className="mt-5 space-y-3 text-sm">
                            <li>
                                <Link
                                    to="/impressum"
                                    data-testid="footer-impressum-link"
                                    className="hover:text-ella-gold transition-colors"
                                >
                                    {t.footer.impressum}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/datenschutz"
                                    data-testid="footer-datenschutz-link"
                                    className="hover:text-ella-gold transition-colors"
                                >
                                    {t.footer.datenschutz}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/agb"
                                    data-testid="footer-agb-link"
                                    className="hover:text-ella-gold transition-colors"
                                >
                                    {t.nav.agb}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="lg:col-span-3">
                        <h4 className="ella-overline-light">{t.footer.contact}</h4>
                        <ul className="mt-5 space-y-3 text-sm">
                            <li>
                                <a
                                    href={`tel:${SITE.contact.phoneIntl.replace(/\s/g, "")}`}
                                    data-testid="footer-phone"
                                    className="inline-flex items-center gap-2 hover:text-ella-gold transition-colors"
                                >
                                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                                    {SITE.contact.phoneDisplay}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={`mailto:${SITE.contact.email}`}
                                    data-testid="footer-email"
                                    className="inline-flex items-center gap-2 hover:text-ella-gold transition-colors"
                                >
                                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                                    {SITE.contact.email}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={SITE.contact.whatsappUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="footer-whatsapp"
                                    className="inline-flex items-center gap-2 hover:text-ella-gold transition-colors"
                                >
                                    <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                                    WhatsApp
                                </a>
                            </li>
                            <li>
                                <a
                                    href={SITE.contact.instagram}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="footer-instagram"
                                    className="inline-flex items-center gap-2 hover:text-ella-gold transition-colors"
                                >
                                    <Instagram className="w-4 h-4" strokeWidth={1.5} />
                                    {SITE.contact.instagramHandle}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={SITE.contact.facebook}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="footer-facebook"
                                    className="inline-flex items-center gap-2 hover:text-ella-gold transition-colors"
                                >
                                    <Facebook className="w-4 h-4" strokeWidth={1.5} />
                                    Facebook
                                </a>
                            </li>
                            <li className="inline-flex items-center gap-2 text-ella-cream/60">
                                <MapPin className="w-4 h-4" strokeWidth={1.5} />
                                {t.footer.address_placeholder}
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-ella-cream/50">
                    <p>
                        © {year} ELLA Online Coaching · {SITE.coachName} · {t.footer.rights}
                    </p>
                    <p className="ella-overline-light">Swiss Premium · Transformation · Growth</p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
